const STAGE_LABELS: Record<string, string> = {
  T1: 'content extraction',
  T2: 'text recognition (OCR)',
  T3: 'indexing',
  T4: 'enrichment',
};

const STAGE_DETAIL_LABELS: Record<string, string> = {
  roles: 'role detection',
  classification: 'classification',
  graph: 'knowledge graph',
};

const ERROR_PATTERNS: Array<{ test: RegExp; format: (match: RegExpMatchArray) => string }> = [
  {
    test: /^Failed stage\(s\):\s*(.+)$/i,
    format: (match) => {
      const stages = match[1]
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean)
        .map(s => STAGE_DETAIL_LABELS[s] ?? s);
      return stages.length === 1
        ? `Processing failed during ${stages[0]}.`
        : `Processing failed during: ${stages.join(', ')}.`;
    },
  },
  {
    test: /^AI processing failed with code:\s*(\d+)$/i,
    format: () => 'Document processing failed. Please try again or contact support.',
  },
  {
    test: /^Processing failed\.?$/i,
    format: () => 'Document processing failed. Please try again.',
  },
  {
    test: /remote server returned an error:\s*\((\d+)\)/i,
    format: (match) => {
      const code = match[1];
      if (code === '404') return 'The source file could not be found.';
      if (code === '403') return 'Access to the source file was denied.';
      if (code === '401') return 'Authentication failed while accessing the source file.';
      return 'Unable to download the file from the source.';
    },
  },
  {
    test: /(timeout|timed out)/i,
    format: () => 'The request timed out. Please try again.',
  },
  {
    test: /(connection refused|unable to connect|network is unreachable)/i,
    format: () => 'Unable to connect to the source. Please check your connection and try again.',
  },
  {
    test: /(unsupported|not supported).*(format|file type|extension)/i,
    format: () => 'This file type is not supported.',
  },
  {
    test: /(password|encrypted|protected).*(pdf|document|file)/i,
    format: () => 'This file appears to be password-protected and could not be processed.',
  },
  {
    test: /(corrupt|damaged|invalid).*(file|document|pdf)/i,
    format: () => 'The file appears to be corrupted or invalid.',
  },
  {
    test: /blob.*not found/i,
    format: () => 'The uploaded file could not be found. Please re-upload and try again.',
  },
];

function parseStagePrefixedError(raw: string): string | null {
  const match = raw.match(/^\[(T[1-4])\]\s*(.+)$/i);
  if (!match) return null;

  const stage = match[1].toUpperCase();
  const detail = match[2].trim();
  const stageLabel = STAGE_LABELS[stage] ?? `stage ${stage}`;

  for (const pattern of ERROR_PATTERNS) {
    const detailMatch = detail.match(pattern.test);
    if (detailMatch) {
      const formatted = pattern.format(detailMatch);
      return formatted.endsWith('.') ? formatted : `${formatted}.`;
    }
  }

  if (/^processing failed/i.test(detail)) {
    return `Processing failed during ${stageLabel}.`;
  }

  return `Processing failed during ${stageLabel}.`;
}

function looksTechnical(message: string): boolean {
  return (
    /^\w+(\.\w+)*Exception:/i.test(message) ||
    /at\s+\w+\./i.test(message) ||
    /stack trace/i.test(message) ||
    message.length > 200
  );
}

/** Converts backend error strings into short, user-friendly messages. */
export function formatDocumentErrorMessage(raw: string | null | undefined): string {
  if (!raw?.trim()) return '';

  const trimmed = raw.trim();

  const stageMessage = parseStagePrefixedError(trimmed);
  if (stageMessage) return stageMessage;

  for (const pattern of ERROR_PATTERNS) {
    const match = trimmed.match(pattern.test);
    if (match) {
      const formatted = pattern.format(match);
      return formatted.endsWith('.') ? formatted : `${formatted}.`;
    }
  }

  if (looksTechnical(trimmed)) {
    return 'An unexpected error occurred while processing this document.';
  }

  const sentence = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return sentence.endsWith('.') ? sentence : `${sentence}.`;
}
